<template>
  <div class="patient-overview-hn-progress-over-time">
    <v-row>
      <v-col cols="6" class="patient-overview-hn-progress-over-time--nutritiongoalselect">
        <span class="patient-overview-hn-progress-over-time--label">{{
            $t('nutrition.progressOverTime.healthyNutritionGoal')
          }}: </span>
        <v-select
            class="patient-overview-hn-progress-over-time--select"
            :items="goalChoices"
            v-model="chosenGoalId"
            outlined
            style="height: 20px"
        >
          <template v-slot:item="{ item }">
            <FoodGroupIcon class="mr-5" :foodgroup-key="item.foodGroup"/>
            {{ item.text }}
          </template>
          <template v-slot:selection="{ item }">
            <FoodGroupIcon class="mr-5" :foodgroup-key="item.foodGroup"/>
            {{ item.text }}
          </template>
        </v-select>
      </v-col>
      <v-col cols="3">
          <span class="patient-overview-hn-progress-over-time--label">
            {{ $ucfirst($t("general.from", {catalog: 'app_mobile'})) }}:
          </span>
        <date-picker
            v-model="chosenStartDate"
            class="patient-overview-hn-progress-over-time--picker"
            :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
            :datePickerAttr="{
                min: minAllowedStartDateString,
                max: maxAllowedStartDateString
              }"
        />
      </v-col>
      <v-col cols="3">
          <span class="patient-overview-hn-progress-over-time--label">
            {{ $ucfirst($t("general.until", {catalog: 'app_mobile'})) }}:
          </span>
        <date-picker
            v-model="chosenEndDate"
            :disabledDateMessage="$ucfirst($t('validation.invalidDate.dateFromEnrolment'))"
            :datePickerAttr="{
                min: minAllowedStartDateString,
                max: maxAllowedStartDateString
              }"
        />
      </v-col>
    </v-row>

    <v-row no-gutters class="patient-overview-hn-progress-over-time__chart-row">
      <v-col cols="10">
        <h3 class="patient-overview-hn-progress-over-time__chart-ytitle">{{ chosenGoal ? chosenGoal.foodGroup.unitLabel : '' }}</h3>
        <apexchart
            type="line"
            height="390"
            :options="chartOptions"
            :series="chartSeries"
        ></apexchart>
      </v-col>
      <v-col cols="2">
        <v-card class="patient-overview-hn-progress-over-time__chart-legend" elevation="0">
          <div class="patient-overview-hn-progress-over-time__legend-item">
            <span class="patient-overview-hn-progress-over-time__legend-color" style="background-color: #b2f7df;">**</span>
            <span>{{ $t('nutrition.weeklyGoals.selectedAsGoal') }}</span>
          </div>
          <div class="patient-overview-hn-progress-over-time__legend-item">
            <span class="patient-overview-hn-progress-over-time__legend-color round" style="background-color: #00e396;">**</span>
            <span>{{ $t('nutrition.weeklyGoals.recommendedAmount') }}</span>
          </div>
          <div class="patient-overview-hn-progress-over-time__legend-item">
            <span class="patient-overview-hn-progress-over-time__legend-color round" style="background-color: #008ffb;">**</span>
            <span>{{ $t('nutrition.weeklyGoals.reportedAmount') }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment';
import FoodGroupIcon from "dashboard/templates/components/Parts/HealthyNutrition/FoodGroupIcon.vue";
import DatePicker from "../../../components/Parts/DatePicker";

export default {
  name: "PatientOverviewHNProgressOverTime",
  components: {FoodGroupIcon, DatePicker},
  props: {
    patientId: {
      type: String,
      required: true,
    },
  },
  data: function () {
    return {
      chosenGoalId: null,
      goals: [],
      chosenStartDate: null,
      chosenEndDate: null,
      myHealthyNutritionWeeks : [],
    };
  },
  async created() {
    this.chosenStartDate = this.$store.getters["patientSummary/lastCompletedSDMEncounterDate"];
    this.chosenEndDate = this.maxAllowedStartDate;

    await this.loadGoals();
    this.loadMyHealthyNutritionWeeks();
  },
  watch: {
    chosenStartDate() {
      if (this.chosenStartDate && this.chosenEndDate && this.chosenEndDate.isBefore(this.chosenStartDate)) {
        this.chosenEndDate = this.chosenStartDate.clone();
      }
      this.loadMyHealthyNutritionWeeks();
    },
    chosenEndDate() {
      if (this.chosenStartDate && this.chosenEndDate && this.chosenStartDate.isAfter(this.chosenEndDate)) {
        this.chosenStartDate = this.chosenEndDate.clone();
      }
      this.loadMyHealthyNutritionWeeks();
    },
    patientId() {
      this.loadMyHealthyNutritionWeeks();
    },
  },
  computed: {
    minAllowedStartDate() {
      let lastVisitDate = this.$store.getters["patientSummary/visit1Date"];

      if (lastVisitDate) {
        return moment(lastVisitDate).startOf('isoWeek');
      }

      return moment().startOf('isoWeek');
    },
    minAllowedStartDateString() {
      return this.minAllowedStartDate ? this.minAllowedStartDate.format('YYYY-MM-DD') : null
    },
    maxAllowedStartDateString() {
      return this.maxAllowedStartDate ? this.maxAllowedStartDate.format('YYYY-MM-DD') : null
    },
    maxAllowedStartDate() {
      return moment().endOf('isoWeek');
    },
    mondayChosenStartDate() {
      if (this.chosenStartDate) {
        return this.chosenStartDate.clone().startOf('isoWeek');
      }

      return null;
    },
    goalChoices() {
      return this.goals.map((goal) => {
        return {
          text:  goal.name.length > 45 ? goal.name.slice(0, 45) + '...' : goal.name,
          value: goal.id,
          foodGroup: goal.foodGroup.key
        }
      })
    },
    chosenGoal() {
      for (let i=0; i<this.goals.length; i++){
        const goal = this.goals[i];

        if (goal.id === this.chosenGoalId) {
          return goal;
        }
      }

      return null;
    },
    chartSeries() {
      return [
        {
          data : this.myHealthyNutritionWeeks.map(week => {
            const goal = this.getChosenAchievedGoalForNutritionWeek(week);
            const planningStartDate = moment.tz(week.planning.startDate, 'YYYY-MM-DD HH:mm:ss', 'UTC').utc();

            return {
              x : planningStartDate,
              y : week.allAnswersGivenInWeek ? goal.score : null
            }
          }),
          name : this.$t('nutrition.weeklyGoals.reportedAmount')
        },
        {
          data : this.myHealthyNutritionWeeks.map(week => {
            const planningStartDate = moment.tz(week.planning.startDate, 'YYYY-MM-DD HH:mm:ss', 'UTC').utc();

            return {
              x : planningStartDate,
              y : 5
            }
          }),
          name : this.$t('nutrition.weeklyGoals.recommendedAmount')
        }
      ];
    },
    chartOptions() {
      let minDate = null, maxDate = null, weekCount = 0, xTickDividor = 1, xTickAmount = 1;

      if (this.myHealthyNutritionWeeks.length) {
        minDate = moment(this.myHealthyNutritionWeeks[0].planning.startDate);
        maxDate = moment(this.myHealthyNutritionWeeks[this.myHealthyNutritionWeeks.length - 1].planning.startDate);
        weekCount = maxDate.diff(minDate, 'weeks') + 1;

        if (weekCount >= 104) {
          xTickDividor = 8;
        }else if (weekCount >= 52) {
          xTickDividor = 4;
        }else if (weekCount >= 26) {
          xTickDividor = 2
        }

        xTickAmount = Math.max(1,Math.ceil(weekCount / xTickDividor) - 1);
      }

      return {
        chart: {
          type: "line",
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          ...this.$app.apexChartHelper.getMonthTranslations(),
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "straight",
        },
        colors: ['#008ffb', '#00e396'],
        grid: {
          padding: {
            left: 75,
          },
        },
        xaxis: {
          type: "datetime",
          title: {
            text: this.$ucfirst(this.$t("alerts.time")),
            style: {
              fontSize: '0.75rem',
            },
          },
          // min: minDateString,
          // max: maxDateString,
          tickAmount: xTickAmount,
          labels: {
            formatter: (val) => {
              const date = moment(val).utc(),
                  endDate = date.clone().endOf('isoWeek'),
                  startDate = endDate.clone().subtract(xTickDividor, 'weeks').add(1, 'day')
                  return `${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`;
            },
            rotate: -20,
            minHeight: 90,
            offsetY: 8,
            rotateAlways: true,
            hideOverlappingLabels: true,
            style: {
              fontSize: '0.75rem',
            }
          }
        },
        tooltip: {
          x: {
            formatter: function(val, opts) {
              const date = moment(val).utc(),
                  endDate = date.clone().endOf('isoWeek'),
                  startDate = endDate.clone().subtract(1, 'weeks').add(1, 'day')
              return `${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`;
            }
          }
        },
        yaxis: {
          labels: {
            formatter: (val) => {
              return this.getLabelForScore(val);
            },
            style: {
              fontSize: '0.75rem',
            },
          },
          min: 0,
          max: 5,
          tickAmount: 5,
        },
        annotations: {
          position: 'back',
          xaxis: this.myHealthyNutritionWeeks.filter(week => {
            const goal = this.getChosenAchievedGoalForNutritionWeek(week);
            return goal && goal.hasPatientGoals && week.planning.wasInActionStage;
          }).map(week => {
            return {
              x: moment(week.planning.startDate).valueOf(),
              x2: moment(week.planning.startDate).add(1, 'weeks').valueOf(),
              fillColor: '#00E396',
            }
          })
        },
        legend: {
          show: false
        },
        markers: {
          size: 4,
          strokeWidth: 1,
          hover: {
            size: 6,
          }
        },
      };
    },
  },
  methods: {
    async loadGoals() {
      this.goals = await this.$app.healthyNutritionManager.getHealthyNutritionGoals()
      this.chosenGoalId = this.goals[0].id;
    },
    async loadMyHealthyNutritionWeeks() {
      const weekRelay = await this.$app.healthyNutritionManager.getHealthyNutritionWeeks(
          this.patientId,
          this.mondayChosenStartDate,
          this.chosenEndDate
      );

      this.myHealthyNutritionWeeks = weekRelay.edges.map(edge => edge.node);
    },
    getChosenAchievedGoalForNutritionWeek(nutritionWeek) {
      for (let i=0; i<nutritionWeek.achievedGoals.length; i++) {
        const achievedGoal = nutritionWeek.achievedGoals[i];

        if (achievedGoal.goal.id === this.chosenGoalId) {
          return achievedGoal;
        }
      }

      return null;
    },
    getLabelForScore(score) {
      if (this.chosenGoal?.associatedEProQuestion.answerSet.answers) {
        const answers = this.chosenGoal?.associatedEProQuestion.answerSet.answers;

        for (let i=0; i<answers.length; i++) {
          if (answers[i].score === score) {
            return answers[i].text;
          }
        }
      }

      return score;
    }
  }
}
</script>

<style lang="scss">
.patient-overview-hn-progress-over-time--label {
  display: block;
  margin-right: 15px;
  float: left;
  font-weight: bold;
  font-size: 1em;
  line-height: 2.5em;
}

.patient-overview-hn-progress-over-time--select {
  float: left;

  .v-input__control, .v-input__slot {
    height: 50px !important;
    min-height: 50px !important;;
  }
  .v-select__selections, .v-select__slot {
    height: 46px !important;;
    min-height: 46px !important;;
  }
}

.patient-overview-hn-progress-over-time__legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.patient-overview-hn-progress-over-time__legend-color {
  width: 60px;
  height: 20px;
  display: inline-block;
  margin-right: 10px;
  border-radius: 2px;
  color: rgba(0,0,0,0);
}

.patient-overview-hn-progress-over-time__legend-color.round {
  width: 20px;
  margin: 0 30px 0 20px;
  border-radius: 50%;
}

.patient-overview-hn-progress-over-time__chart-legend {
  margin: 28px 0 0 15px;
  padding: 5px 15px;
}

.patient-overview-hn-progress-over-time__chart-row {
  padding: 30px 0 0 30px;
}

.patient-overview-hn-progress-over-time__chart-ytitle {
  transform: rotate(270deg) translate(-140px, -173px);
  width: 300px;
  float: left;
  text-align: center;
  font-weight: normal;
  font-size: 1em;
}
</style>
